import React, { FC } from 'react'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import { AllocationDetails, AllocationHistory } from '../../oms-create-order/types'

interface Props {
  allocation: AllocationDetails | undefined
  allocationHistory: AllocationHistory[] | undefined
  currentQuantity: number
  productName: string
  productSku: string
}

const AllocationPill: FC<Props> = ({ allocation, allocationHistory, currentQuantity, productName, productSku }) => {
  const isMissing = !allocation
  const net_allocated_quantity = allocation?.net_allocated_quantity ?? allocation?.allocated_quantity ?? 0
  const history = allocationHistory || []
  const isFullyAllocated = !isMissing && net_allocated_quantity >= currentQuantity
  const isPartiallyAllocated = !isMissing && net_allocated_quantity > 0 && net_allocated_quantity < currentQuantity

  let bg = '#DC2626'
  let color = '#ffffff'
  let border = '#DC2626'
  let label = '✗ Unallocated'
  
  if (isFullyAllocated) {
    bg = '#16A34A'
    border = '#16A34A'
    label = '✔ Fully Allocated'
  } else if (isPartiallyAllocated) {
    bg = '#D97706'
    border = '#D97706'
    label = '⚡ Partially'
  }

  const pill = (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '6px',
      padding: '4px 10px',
      borderRadius: '6px',
      backgroundColor: bg,
      border: `1px solid ${border}`,
      color: color,
      fontSize: '12px',
      fontWeight: 600,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      minWidth: '140px'
    }}>
      <span>{label}</span>
      <span style={{
        backgroundColor: 'rgba(255,255,255,0.25)',
        padding: '1px 6px',
        borderRadius: '10px',
        fontSize: '11px',
        fontWeight: 700,
        color: '#ffffff'
      }}>
        {net_allocated_quantity}/{currentQuantity ?? 0}
      </span>
    </div>
  )

  const popover = (
    <Popover id={`popover-alloc-${Math.random()}`} style={{ maxWidth: '400px', border: 'none', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
      <Popover.Header as="div" style={{ backgroundColor: '#f8f9fc', borderBottom: '1px solid #eef0f2', margin: 0, padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ color: '#343a40', fontSize: '13px', fontWeight: 700 }}>Allocation Trail</div>
        <div style={{ fontSize: '11px', color: '#6c757d', lineHeight: '1.3', textAlign: 'right', maxWidth: '60%' }}>
          <div style={{ fontWeight: 600, color: '#495057', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{productName || 'Unknown Product'}</div>
          {productSku && <div style={{ marginTop: '1px' }}>SKU: {productSku}</div>}
        </div>
      </Popover.Header>
      <Popover.Body style={{ padding: 0 }}>
        <div style={{ maxHeight: '25vh', overflowY: 'auto', padding: '18px', backgroundColor: '#fff', minWidth: '320px' }}>
          {history.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#6c757d', padding: '10px 0', fontSize: '12px' }}>
              No allocation history available.
            </div>
          ) : (
            [...history].reverse().map((h, index) => {
              const dateObj = new Date(h.created_at)
              const dateStr = dateObj.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
              const timeStr = dateObj.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
              const isLast = index === history.length - 1

              return (
                <div key={h.id} style={{ display: 'flex', position: 'relative', marginBottom: isLast ? 0 : '18px' }}>
                  {/* Date/Time (Left) */}
                  <div style={{ width: '65px', textAlign: 'right', paddingRight: '14px', flexShrink: 0, paddingTop: '3px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#495057' }}>{dateStr}</div>
                    <div style={{ fontSize: '11px', color: '#adb5bd', marginTop: '2px' }}>{timeStr}</div>
                  </div>

                  {/* Timeline Line & Dot */}
                  <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{
                      width: '12px', height: '12px', backgroundColor: h.is_allocated ? '#37b24d' : '#f03e3e',
                      borderRadius: '50%', border: '2px solid #fff', zIndex: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                      marginTop: '5px'
                    }}></div>
                    {!isLast && <div style={{ position: 'absolute', top: '17px', bottom: '-23px', left: '5px', borderLeft: '2px solid #e9ecef' }}></div>}
                  </div>

                  {/* Content (Right) */}
                  <div style={{ flex: 1, paddingLeft: '18px' }}>
                    <div style={{
                      backgroundColor: h.is_allocated ? '#f8fdf9' : '#fff5f5', padding: '10px 14px', borderRadius: '8px',
                      border: '1px solid', borderColor: h.is_allocated ? '#e3fae8' : '#ffe3e3', fontSize: '13px', lineHeight: '1.4',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{
                          color: h.is_allocated ? '#2b8a3e' : '#c92a2a',
                          backgroundColor: h.is_allocated ? '#d3f9d8' : '#ffe3e3',
                          padding: '3px 8px', borderRadius: '6px', fontWeight: 700, fontSize: '11px'
                        }}>
                          {h.is_allocated ? '+' : '-'}{h.allocated_quantity}
                        </span>
                        <span style={{ color: '#495057', fontWeight: 500, fontSize: '12px' }}>
                          {h.warehouse?.api_name || 'Unknown Location'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </Popover.Body>
    </Popover>
  )

  return (
    <OverlayTrigger trigger="click" rootClose placement="auto" overlay={popover}>
      {pill}
    </OverlayTrigger>
  )
}

export default AllocationPill
